import { Location } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, concat, Observable, of, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';

import { CtaVariantEnum } from '~/component/call-to-action/enums/variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { TitleService } from '~/core/services/title/title.service';
import { MessagingUserInterface } from '~/messaging/interfaces/user.interface';
import { MessagingService } from '~/messaging/services/messaging.service';
import { TalkService } from '~/messaging/services/talk.service';
import { BookingItemsGroupByUserIdInterface } from '~/organizer/shared/services/activity/interfaces/booking-items-group-by-user-id.interface';

@Component({
  selector: 'app-organizer-messaging-contact',
  templateUrl: './contact.component.html',
})
export class OrganizerMessagingPageContactComponent
  implements OnInit, OnDestroy
{
  @ViewChild('talkjsContainer') public talkjsContainer!: ElementRef;

  public readonly backButtonVariant = CtaVariantEnum.Link2;
  public readonly iconArrowLeftAlt = VtmnPlayIconTypeEnum.ArrowLeft;
  public readonly ctaButtonType = CtaButtonTypeEnum;

  private bookingItemsGroupByUserId: BookingItemsGroupByUserIdInterface[] = [];
  private isLoading = false;
  private subscription$: Subscription;

  public activity: Activity;
  public form: FormGroup;
  public timeslot: Timeslot;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly redirectionService: RedirectionService,
    private readonly talkService: TalkService,
    private readonly titleService: TitleService,
    public readonly location: Location,
    private readonly messagingService: MessagingService
  ) {}

  public ngOnDestroy(): void {
    if (this.subscription$) {
      this.subscription$.unsubscribe();
    }
  }

  public ngOnInit(): void {
    this.titleService.setPageTitle('seo.organizer.messaging.contact.title');

    this.form = new FormGroup({
      message: new FormControl('', Validators.required),
    });

    const { bookingItemsGroupByUserId, timeslotData } =
      this.activatedRoute.snapshot.data;

    this.bookingItemsGroupByUserId = bookingItemsGroupByUserId;
    this.timeslot = timeslotData.timeslot;
    this.activity = timeslotData.activity;
  }

  private get message(): string {
    return this.form.get('message').value;
  }

  public get backLink(): string {
    return `/organizer.timeslot.manage/${this.timeslot.identifier}`;
  }

  public get isSubmitDisabled(): boolean {
    return this.form.invalid || this.isLoading;
  }

  public get messageErrors(): ValidationErrors {
    const message = this.form.get('message');

    return message?.touched && message?.errors;
  }

  public submit(): void {
    this.isLoading = true;

    this.messagingService.storeActivityData(
      this.messagingService.buildActivityData(this.activity, this.timeslot)
    );

    const recipients: MessagingUserInterface[] =
      this.bookingItemsGroupByUserId.map((bookingItemGroupByUserId) => ({
        name: bookingItemGroupByUserId.bookingItems?.[0]?.attendees?.[0]?.name,
        identifier: bookingItemGroupByUserId.userId,
      }));

    const subscriptions$: Observable<void>[] = [];

    recipients.forEach((recipient) => {
      subscriptions$.push(
        this.talkService.createCurrentSession().pipe(
          mergeMap((session) =>
            combineLatest([
              this.talkService.createInbox(session, recipient),
              of(session),
            ])
          ),
          mergeMap(([inbox, session]) =>
            combineLatest([
              inbox.mount(this.talkjsContainer.nativeElement),
              of(session),
            ])
          ),
          mergeMap(([_, session]) =>
            this.talkService.sendMessage(session, recipient, this.message)
          )
        )
      );
    });

    this.subscription$ = concat(...subscriptions$).subscribe({
      next: () => {},
      error: (err) => console.error('Observer got an error: ' + err),
      complete: () => {
        this.isLoading = false;
        this.redirectionService.redirect('/organizer.messaging.inbox');
      },
    });
  }
}
